import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Workflow,
  Play,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Plus,
  Trash2,
  Calendar,
  Activity,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { automationApi } from '@/api/automation';
import type {
  PipelineTypeInfo,
  PipelineExecutionListItem,
  PipelineScheduleListItem,
  AutomationSummary,
  PipelineRunRequest,
  PipelineScheduleCreate,
} from '@/types/data';
import { useAuthStore } from '@/stores/authStore';

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  pending: { label: '待機中', color: 'text-gray-500 bg-gray-100', icon: Clock },
  running: { label: '実行中', color: 'text-blue-500 bg-blue-100', icon: Loader2 },
  completed: { label: '完了', color: 'text-green-500 bg-green-100', icon: CheckCircle2 },
  failed: { label: '失敗', color: 'text-red-500 bg-red-100', icon: XCircle },
  cancelled: { label: 'キャンセル', color: 'text-yellow-500 bg-yellow-100', icon: AlertCircle },
};

export default function Automation() {
  const queryClient = useQueryClient();
  const { accessibleCompanies } = useAuthStore();

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'executions' | 'schedules'>('summary');
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [selectedPipelineType, setSelectedPipelineType] = useState<string>('full');

  // 初期化
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // パイプラインタイプ一覧
  const { data: pipelineTypes } = useQuery({
    queryKey: ['pipeline-types'],
    queryFn: automationApi.getPipelineTypes,
  });

  // サマリー取得
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['automation-summary'],
    queryFn: automationApi.getSummary,
  });

  // 実行履歴取得
  const { data: executions, isLoading: executionsLoading } = useQuery({
    queryKey: ['automation-executions', selectedCompanyId],
    queryFn: () =>
      automationApi.listExecutions({
        company_id: selectedCompanyId || undefined,
        limit: 50,
      }),
  });

  // スケジュール取得
  const { data: schedules, isLoading: schedulesLoading } = useQuery({
    queryKey: ['automation-schedules', selectedCompanyId],
    queryFn: () =>
      automationApi.listSchedules({
        company_id: selectedCompanyId || undefined,
        limit: 50,
      }),
  });

  // パイプライン実行
  const runMutation = useMutation({
    mutationFn: (request: PipelineRunRequest) => automationApi.runPipeline(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-executions'] });
      queryClient.invalidateQueries({ queryKey: ['automation-summary'] });
      setShowRunDialog(false);
    },
  });

  // スケジュール作成
  const createScheduleMutation = useMutation({
    mutationFn: (data: PipelineScheduleCreate) => automationApi.createSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-schedules'] });
      setShowScheduleDialog(false);
    },
  });

  // スケジュール削除
  const deleteScheduleMutation = useMutation({
    mutationFn: (scheduleId: number) => automationApi.deleteSchedule(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-schedules'] });
    },
  });

  const handleRunPipeline = useCallback(() => {
    if (!selectedCompanyId) return;
    runMutation.mutate({
      company_id: selectedCompanyId,
      pipeline_type: selectedPipelineType,
    });
  }, [selectedCompanyId, selectedPipelineType, runMutation]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ja-JP', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}秒`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}分${secs}秒`;
  };

  const getPipelineTypeName = (type: string) => {
    return pipelineTypes?.find((p) => p.type === type)?.name || type;
  };

  const renderSummaryTab = (summaryData: AutomationSummary) => (
    <div className="space-y-6">
      {/* 概要カード */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">今日の実行数</p>
              <p className="text-2xl font-bold text-gray-900">{summaryData.total_executions_today}</p>
            </div>
            <Activity className="w-10 h-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">実行中</p>
              <p className="text-2xl font-bold text-blue-600">{summaryData.running_pipelines ?? 0}</p>
            </div>
            <Loader2 className={`w-10 h-10 text-blue-500 ${(summaryData.running_pipelines ?? 0) > 0 ? 'animate-spin' : ''}`} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">今日の失敗</p>
              <p className="text-2xl font-bold text-red-600">{summaryData.failed_today ?? 0}</p>
            </div>
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">週間成功率</p>
              <p className="text-2xl font-bold text-green-600">{(summaryData.success_rate_week ?? 0).toFixed(1)}%</p>
            </div>
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
        </div>
      </div>

      {/* パイプラインタイプ別 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">パイプラインタイプ別（週間）</h3>
          <div className="space-y-3">
            {Object.entries(summaryData.by_pipeline_type ?? {}).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="text-gray-700">{getPipelineTypeName(type)}</span>
                <span className="font-medium text-gray-900">{count}回</span>
              </div>
            ))}
            {Object.keys(summaryData.by_pipeline_type ?? {}).length === 0 && (
              <p className="text-gray-500 text-sm">データがありません</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">最近の実行</h3>
          <div className="space-y-3">
            {(summaryData.recent_executions ?? []).slice(0, 5).map((exec) => {
              const statusConfig = STATUS_CONFIG[exec.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              return (
                <div key={exec.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center gap-2">
                    <StatusIcon className={`w-4 h-4 ${statusConfig.color.split(' ')[0]}`} />
                    <span className="text-sm font-medium">{getPipelineTypeName(exec.pipeline_type)}</span>
                  </div>
                  <span className="text-xs text-gray-500">{formatDate(exec.created_at)}</span>
                </div>
              );
            })}
            {(summaryData.recent_executions ?? []).length === 0 && (
              <p className="text-gray-500 text-sm">実行履歴がありません</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderExecutionsTab = (executionsList: PipelineExecutionListItem[]) => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">実行履歴</h3>
        <button
          onClick={() => setShowRunDialog(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          <Play className="w-4 h-4" />
          パイプライン実行
        </button>
      </div>

      {executionsList.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Workflow className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">実行履歴がありません</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">タイプ</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ステータス</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">進捗</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">実行時間</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">開始日時</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">トリガー</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {executionsList.map((exec) => {
                const statusConfig = STATUS_CONFIG[exec.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                return (
                  <tr key={exec.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">#{exec.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{getPipelineTypeName(exec.pipeline_type)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                        <StatusIcon className={`w-3 h-3 ${exec.status === 'running' ? 'animate-spin' : ''}`} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {exec.completed_steps}/{exec.total_steps}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDuration(exec.duration_seconds)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(exec.started_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{exec.triggered_by}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSchedulesTab = (schedulesList: PipelineScheduleListItem[]) => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">スケジュール設定</h3>
        <button
          onClick={() => setShowScheduleDialog(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" />
          スケジュール追加
        </button>
      </div>

      {schedulesList.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">スケジュールがありません</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {schedulesList.map((schedule) => (
            <div key={schedule.id} className="bg-white rounded-lg shadow p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-gray-900">{schedule.name}</h4>
                  <p className="text-sm text-gray-500">{getPipelineTypeName(schedule.pipeline_type)}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  schedule.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {schedule.is_active ? '有効' : '無効'}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Cron: {schedule.schedule_cron}</span>
                </div>
                {schedule.last_run_at && (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>最終実行: {formatDate(schedule.last_run_at)}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => deleteScheduleMutation.mutate(schedule.id)}
                  disabled={deleteScheduleMutation.isPending}
                  className="flex-1 text-sm text-red-600 hover:text-red-700 flex items-center justify-center gap-1"
                >
                  <Trash2 className="w-4 h-4" />
                  削除
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* ヘッダー */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Workflow className="w-7 h-7 text-purple-500" />
              自動化パイプライン
            </h1>
            <p className="text-gray-500 mt-1">
              データ取得から分析、レポート生成までの自動化管理
            </p>
          </div>

          {accessibleCompanies.length > 1 && (
            <select
              value={selectedCompanyId || ''}
              onChange={(e) => setSelectedCompanyId(Number(e.target.value) || null)}
              className="border rounded-lg px-3 py-2"
            >
              <option value="">全法人</option>
              {accessibleCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name || c.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* タブ */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'summary'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            サマリー
          </button>
          <button
            onClick={() => setActiveTab('executions')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'executions'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            実行履歴
          </button>
          <button
            onClick={() => setActiveTab('schedules')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'schedules'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            スケジュール
          </button>
        </div>

        {/* コンテンツ */}
        {summaryLoading || executionsLoading || schedulesLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {activeTab === 'summary' && summary && renderSummaryTab(summary)}
            {activeTab === 'executions' && executions && renderExecutionsTab(executions)}
            {activeTab === 'schedules' && schedules && renderSchedulesTab(schedules)}
          </>
        )}

        {/* パイプライン実行ダイアログ */}
        {showRunDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">パイプライン実行</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
                  <select
                    value={selectedCompanyId || ''}
                    onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">法人を選択</option>
                    {accessibleCompanies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.display_name || c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">パイプラインタイプ</label>
                  <select
                    value={selectedPipelineType}
                    onChange={(e) => setSelectedPipelineType(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {pipelineTypes?.map((type) => (
                      <option key={type.type} value={type.type}>
                        {type.name} - {type.description}
                      </option>
                    ))}
                  </select>
                </div>

                {pipelineTypes?.find((p) => p.type === selectedPipelineType) && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">実行ステップ:</p>
                    <ul className="space-y-1">
                      {pipelineTypes
                        .find((p) => p.type === selectedPipelineType)
                        ?.steps.map((step, index) => (
                          <li key={step.name} className="text-sm text-gray-600 flex items-center gap-2">
                            <ChevronRight className="w-3 h-3" />
                            {index + 1}. {step.description}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowRunDialog(false)}
                  className="flex-1 px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleRunPipeline}
                  disabled={!selectedCompanyId || runMutation.isPending}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {runMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      実行中...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      実行
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* スケジュール作成ダイアログ */}
        {showScheduleDialog && (
          <ScheduleDialog
            companies={accessibleCompanies}
            pipelineTypes={pipelineTypes || []}
            onClose={() => setShowScheduleDialog(false)}
            onSubmit={(data) => createScheduleMutation.mutate(data)}
            isLoading={createScheduleMutation.isPending}
          />
        )}
      </div>
    </div>
  );
}

// スケジュール作成ダイアログコンポーネント
function ScheduleDialog({
  companies,
  pipelineTypes,
  onClose,
  onSubmit,
  isLoading,
}: {
  companies: Array<{ id: number; name: string; display_name?: string }>;
  pipelineTypes: PipelineTypeInfo[];
  onClose: () => void;
  onSubmit: (data: PipelineScheduleCreate) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<PipelineScheduleCreate>({
    company_id: companies[0]?.id || 0,
    name: '',
    pipeline_type: 'full',
    schedule_cron: '0 1 * * *',
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.company_id) return;
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">スケジュール作成</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">スケジュール名</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="例: 日次データ取得"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
            <select
              value={formData.company_id}
              onChange={(e) => setFormData({ ...formData, company_id: Number(e.target.value) })}
              className="w-full border rounded-lg px-3 py-2"
            >
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name || c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">パイプラインタイプ</label>
            <select
              value={formData.pipeline_type}
              onChange={(e) => setFormData({ ...formData, pipeline_type: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            >
              {pipelineTypes.map((type) => (
                <option key={type.type} value={type.type}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cron式</label>
            <input
              type="text"
              value={formData.schedule_cron}
              onChange={(e) => setFormData({ ...formData, schedule_cron: e.target.value })}
              placeholder="0 1 * * *"
              className="w-full border rounded-lg px-3 py-2 font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">例: 0 1 * * * = 毎日1:00 AM</p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
          >
            キャンセル
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.name || !formData.company_id || isLoading}
            className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                作成中...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                作成
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
